import React, { useEffect, useMemo, useState } from 'react';
import PurchaseQuote from './PurchaseQuote';
import axios from 'axios';
import {
  ShoppingCart,
  Package,
  Users,
  Plus,
  Trash2,
  Eye,
  Printer,
  Pencil,
  Search,
  RefreshCw,
  X,
  Save,
  History,
  Receipt,
  TrendingUp,
  Sparkles,
  Loader2
} from 'lucide-react';
import { API } from '../App';

const PRODUCT_CATEGORIES = [
  'Repuestos',
  'Lubricantes',
  'Filtros',
  'Herramientas',
  'Ferretería',
  'Materiales',
  'Eléctricos',
  'Soldadura',
  'Construcción',
  'Pintura',
  'EPP / Seguridad',
  'Limpieza',
  'Oficina',
  'Jardinería',
  'Otros'
];

const UNITS = [
  'Unidad',
  'Pieza',
  'Juego',
  'Par',
  'Litro',
  'Galón',
  'Kilogramo',
  'Gramo',
  'Metro',
  'Metro cuadrado',
  'Metro cúbico',
  'Rollo',
  'Caja',
  'Bolsa',
  'Saco',
  'Bidón',
  'Tambor',
  'Plancha',
  'Barra',
  'Carrete',
  'Otro'
];

const money = (n) =>
  `Bs ${Number(n || 0).toLocaleString('es-BO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const emptyItem = () => ({
  product_id: '',
  product_name: '',
  category: '',
  quantity: 1,
  unit: 'unidad',
  unit_price: 0,
  machinery_id: '',
  machinery_name: '',
  notes: ''
});

export default function Purchases() {
  const [tab, setTab] = useState('compras');
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [machinery, setMachinery] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPurchase, setShowPurchase] = useState(false);
  const [editingPurchaseId, setEditingPurchaseId] = useState(null);
  const [showProduct, setShowProduct] = useState(false);
  const [showSupplier, setShowSupplier] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [search, setSearch] = useState('');
  const [purchaseSearch, setPurchaseSearch] = useState('');
  const [error, setError] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFileInputKey, setAiFileInputKey] = useState(0);

  const [reportFilters, setReportFilters] = useState({
    date_from: '',
    date_to: '',
    product_id: '',
    supplier_id: '',
    buyer_id: '',
    paid_by_id: '',
    payment_method: ''
  });

  const [purchase, setPurchase] = useState({
    purchase_date: new Date().toISOString().slice(0, 10),
    supplier_id: '',
    supplier_name: '',
    supplier_nit: '',
    supplier_phone: '',
    supplier_whatsapp: '',
    supplier_contact: '',
    supplier_address: '',
    supplier_notes: '',
    invoice_number: '',
    payment_method: '',
    buyer_id: '',
    buyer_name: '',
    paid_by_id: '',
    paid_by_name: '',
    request_id: '',
    notes: '',
    items: [emptyItem()]
  });

  const [product, setProduct] = useState({
    name: '',
    category: '',
    brand: '',
    part_number: '',
    unit: 'unidad',
    description: ''
  });

  const [supplier, setSupplier] = useState({
    name: '',
    nit: '',
    phone: '',
    whatsapp: '',
    contact: '',
    address: '',
    notes: ''
  });

  const importPurchaseWithAI = async (file) => {
    if (!file) return;

    setAiLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/purchases/ai/extract`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 120000
        }
      );

      const extracted = response.data?.data || response.data || {};

      const supplierData = extracted.supplier || {};
      const extractedItems = Array.isArray(extracted.items)
        ? extracted.items
        : [];

      const matchedSupplier = suppliers.find(s => {
        const a = String(s.name || '').trim().toLowerCase();
        const b = String(supplierData.name || '').trim().toLowerCase();

        return a && b && (
          a === b ||
          a.includes(b) ||
          b.includes(a)
        );
      });

      const mappedItems = extractedItems.map(item => {
        const productName = String(
          item.product_name || item.name || ''
        ).trim();

        const matchedProduct = products.find(p => {
          const a = String(p.name || '').trim().toLowerCase();
          const b = productName.toLowerCase();

          return a && b && (
            a === b ||
            a.includes(b) ||
            b.includes(a)
          );
        });

        return {
          product_id: matchedProduct?.id || '',
          product_name: matchedProduct?.name || productName,
          category: item.category || matchedProduct?.category || '',
          brand: item.brand || matchedProduct?.brand || '',
          part_number: item.part_number || matchedProduct?.part_number || '',
          quantity: Number(item.quantity || 1),
          unit: item.unit || matchedProduct?.unit || 'unidad',
          unit_price: Number(
            item.unit_price || item.price || 0
          ),
          machinery_id: '',
          machinery_name: '',
          notes: item.notes || ''
        };
      });

      setPurchase({
        purchase_date:
          extracted.purchase_date ||
          new Date().toISOString().slice(0, 10),

        supplier_id: matchedSupplier?.id || '',
        supplier_name:
          matchedSupplier?.name ||
          supplierData.name ||
          '',

        supplier_nit:
          supplierData.nit ||
          '',

        supplier_phone:
          supplierData.phone ||
          '',

        supplier_whatsapp:
          supplierData.whatsapp ||
          '',

        supplier_contact:
          supplierData.contact ||
          '',

        supplier_address:
          supplierData.address ||
          '',

        supplier_notes:
          supplierData.notes ||
          '',

        invoice_number:
          extracted.invoice_number ||
          '',

        payment_method:
          extracted.payment_method ||
          '',

        buyer_id: '',
        buyer_name: '',
        paid_by_id: '',
        paid_by_name: '',
        request_id: '',

        notes:
          extracted.notes ||
          'Compra importada mediante IA. Revisar antes de guardar.',

        items: mappedItems.length
          ? mappedItems
          : [emptyItem()]
      });

      setShowPurchase(true);
      setAiFileInputKey(k => k + 1);

    } catch (e) {
      console.error('Error importando compra con IA:', e);

      setError(
        e.response?.data?.detail ||
        'No se pudo analizar el documento con IA'
      );
    } finally {
      setAiLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [p, pr, s, u, st, m, mr] = await Promise.all([
        axios.get(`${API}/purchases`),
        axios.get(`${API}/products`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/purchases/stats/summary`),
        axios.get(`${API}/machinery/summary`).catch(() => ({ data: [] })),
        axios.get(`${API}/material-requests`).catch(() => ({ data: [] }))
      ]);
      setPurchases(p.data || []);
      setProducts(pr.data || []);
      setSuppliers(s.data || []);
      setUsers(u.data || []);
      setSummary(st.data || null);
      setMachinery(m.data || []);
      setRequests(mr.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cargar el módulo de compras');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const total = useMemo(
    () => purchase.items.reduce(
      (sum, i) => sum + Number(i.quantity || 0) * Number(i.unit_price || 0),
      0
    ),
    [purchase.items]
  );

  const filteredProducts = products.filter(p =>
    `${p.name} ${p.brand || ''} ${p.part_number || ''}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const filteredPurchases = useMemo(() => {
    const term = purchaseSearch.trim().toLowerCase();

    if (!term) return purchases;

    return purchases.filter(p => {
      const itemText = (p.items || [])
        .map(item =>
          `${item.product_name || ''} ${item.category || ''} ${item.unit || ''} ${item.notes || ''}`
        )
        .join(' ');

      const text = [
        p.purchase_date,
        p.supplier_name,
        p.invoice_number,
        p.buyer_name,
        p.paid_by_name,
        p.created_by_name,
        p.payment_method,
        p.notes,
        itemText
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return text.includes(term);
    });
  }, [purchases, purchaseSearch]);

  const reportData = useMemo(() => {
    const filtered = purchases.filter(p => {
      if (
        reportFilters.date_from &&
        String(p.purchase_date || '') < reportFilters.date_from
      ) {
        return false;
      }

      if (
        reportFilters.date_to &&
        String(p.purchase_date || '') > reportFilters.date_to
      ) {
        return false;
      }

      if (
        reportFilters.supplier_id &&
        String(p.supplier_id || '') !== String(reportFilters.supplier_id)
      ) {
        return false;
      }

      if (
        reportFilters.buyer_id &&
        String(p.buyer_id || '') !== String(reportFilters.buyer_id)
      ) {
        return false;
      }

      if (
        reportFilters.paid_by_id &&
        String(p.paid_by_id || '') !== String(reportFilters.paid_by_id)
      ) {
        return false;
      }

      if (
        reportFilters.payment_method &&
        String(p.payment_method || '') !== String(reportFilters.payment_method)
      ) {
        return false;
      }

      return true;
    });

    const rows = [];

    filtered.forEach(p => {
      (p.items || []).forEach(item => {
        if (
          reportFilters.product_id &&
          String(item.product_id || '') !== String(reportFilters.product_id)
        ) {
          return;
        }

        const quantity = Number(item.quantity || 0);
        const unitPrice = Number(item.unit_price || 0);
        const total = Number(
          item.total != null
            ? item.total
            : quantity * unitPrice
        );

        rows.push({
          purchase_id: p.id,
          purchase_date: p.purchase_date,
          supplier_name: p.supplier_name || 'Sin proveedor',
          invoice_number: p.invoice_number || '—',
          buyer_name: p.buyer_name || '—',
          paid_by_name: p.paid_by_name || '—',
          payment_method: p.payment_method || '—',
          product_id: item.product_id || '',
          product_name: item.product_name || 'Producto sin nombre',
          category: item.category || '',
          quantity,
          unit: item.unit || '',
          unit_price: unitPrice,
          total,
          notes: item.notes || ''
        });
      });
    });

    const purchaseIds = new Set(
      rows.map(row => row.purchase_id)
    );

    const quantity = rows.reduce(
      (sum, row) => sum + row.quantity,
      0
    );

    const total = rows.reduce(
      (sum, row) => sum + row.total,
      0
    );

    const unitPriceValues = rows
      .map(row => row.unit_price)
      .filter(value => Number.isFinite(value) && value > 0);

    const averageUnitPrice = unitPriceValues.length
      ? unitPriceValues.reduce((sum, value) => sum + value, 0) /
        unitPriceValues.length
      : 0;

    const minUnitPrice = unitPriceValues.length
      ? Math.min(...unitPriceValues)
      : 0;

    const maxUnitPrice = unitPriceValues.length
      ? Math.max(...unitPriceValues)
      : 0;

    return {
      rows,
      purchase_count: purchaseIds.size,
      quantity,
      total,
      average_unit_price: averageUnitPrice,
      min_unit_price: minUnitPrice,
      max_unit_price: maxUnitPrice
    };
  }, [purchases, reportFilters]);

  const formatPurchaseDate = (date) => {
    if (!date) return '—';

    const parts = String(date).split('-');

    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }

    return date;
  };

  const getPurchaseItemsPreview = (purchase) => {
    const items = purchase.items || [];

    if (!items.length) return 'Sin productos';

    const firstItems = items.slice(0, 2).map(item => {
      const name = item.product_name || 'Producto sin nombre';
      const quantity = Number(item.quantity || 0);
      const unit = item.unit || '';

      return `${name} · ${quantity} ${unit}`.trim();
    });

    const remaining = items.length - firstItems.length;

    return remaining > 0
      ? `${firstItems.join(' | ')} + ${remaining} más`
      : firstItems.join(' | ');
  };

  const addItem = () =>
    setPurchase(p => ({ ...p, items: [...p.items, emptyItem()] }));

  const removeItem = (index) =>
    setPurchase(p => ({
      ...p,
      items: p.items.filter((_, i) => i !== index)
    }));

  const updateItem = (index, field, value) => {
    setPurchase(p => ({
      ...p,
      items: p.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const selectProduct = (index, productId) => {
    const p = products.find(x => x.id === productId);
    if (!p) return;

    updateItem(index, 'product_id', p.id);
    updateItem(index, 'product_name', p.name);
    updateItem(index, 'category', p.category || '');
    updateItem(index, 'unit', p.unit || 'unidad');
    updateItem(index, 'unit_price', p.last_price || 0);
  };

  const savePurchase = async (e) => {
    e.preventDefault();
    setError('');

    if (!purchase.items.length) {
      setError('Agrega al menos un producto.');
      return;
    }

    try {
      if (editingPurchaseId) {
        await axios.put(`${API}/purchases/${editingPurchaseId}`, purchase);
      } else {
        await axios.post(`${API}/purchases`, purchase);
      }

      setShowPurchase(false);
      setEditingPurchaseId(null);
      setPurchase({
        purchase_date: new Date().toISOString().slice(0, 10),
        supplier_id: '',
        supplier_name: '',
        supplier_nit: '',
        supplier_phone: '',
        supplier_whatsapp: '',
        supplier_contact: '',
        supplier_address: '',
        supplier_notes: '',
        invoice_number: '',
        payment_method: '',
        buyer_id: '',
        buyer_name: '',
        paid_by_id: '',
        paid_by_name: '',
        request_id: '',
        notes: '',
        items: [emptyItem()]
      });
      await load();
    } catch (e) {
      setError(
        e.response?.data?.detail ||
        (editingPurchaseId
          ? 'No se pudo actualizar la compra'
          : 'No se pudo registrar la compra')
      );
    }
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/products`, product);
      setShowProduct(false);
      setProduct({
        name: '',
        category: '',
        brand: '',
        part_number: '',
        unit: 'unidad',
        description: ''
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo crear el producto');
    }
  };

  const saveSupplier = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/suppliers`, supplier);
      setShowSupplier(false);
      setSupplier({
        name: '',
        nit: '',
        phone: '',
        whatsapp: '',
        contact: '',
        address: '',
        notes: ''
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo crear el proveedor');
    }
  };

  const deletePurchase = async (id) => {
    if (!window.confirm('¿Eliminar esta compra? Se recalculará el historial de precios.')) return;
    try {
      await axios.delete(`${API}/purchases/${id}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo eliminar la compra');
    }
  };

  const editPurchase = (p) => {
    setError('');

    setPurchase({
      purchase_date: p.purchase_date || new Date().toISOString().slice(0, 10),
      supplier_id: p.supplier_id || '',
      supplier_name: p.supplier_name || '',
      supplier_nit: p.supplier_nit || '',
      supplier_phone: p.supplier_phone || '',
      supplier_whatsapp: p.supplier_whatsapp || '',
      supplier_contact: p.supplier_contact || '',
      supplier_address: p.supplier_address || '',
      supplier_notes: p.supplier_notes || '',
      invoice_number: p.invoice_number || '',
      payment_method: p.payment_method || '',
      buyer_id: p.buyer_id || '',
      buyer_name: p.buyer_name || '',
      paid_by_id: p.paid_by_id || '',
      paid_by_name: p.paid_by_name || '',
      request_id: p.request_id || '',
      notes: p.notes || '',
      items: (p.items || []).map(item => ({
        product_id: item.product_id || '',
        product_name: item.product_name || '',
        category: item.category || '',
        brand: item.brand || '',
        part_number: item.part_number || '',
        quantity: Number(item.quantity || 0),
        unit: item.unit || 'unidad',
        unit_price: Number(item.unit_price || 0),
        machinery_id: item.machinery_id || '',
        machinery_name: item.machinery_name || '',
        notes: item.notes || ''
      }))
    });

    setEditingPurchaseId(p.id);
    setShowPurchase(true);
  };

  const viewPurchase = (purchase) => {
    setSelectedPurchase(purchase);
  };

  const printPurchase = (purchase) => {
    const supplier = suppliers.find(
      s => s.id === purchase.supplier_id
    );

    const escapeHtml = (value) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const formatDate = (value) => {
      if (!value) return '—';

      const parts = String(value).split('-');

      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }

      return value;
    };

    const formatMoney = (value) =>
      Number(value || 0).toLocaleString('es-BO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

    const itemsHtml = (purchase.items || []).map(item => {
      const quantity = Number(item.quantity || 0);
      const unitPrice = Number(item.unit_price || 0);
      const subtotal = quantity * unitPrice;

      return `
        <tr>
          <td>${escapeHtml(item.product_name || '—')}</td>
          <td>${escapeHtml(item.category || '—')}</td>
          <td class="center">${escapeHtml(quantity)}</td>
          <td>${escapeHtml(item.unit || '—')}</td>
          <td class="money">Bs ${formatMoney(unitPrice)}</td>
          <td class="money">Bs ${formatMoney(subtotal)}</td>
        </tr>
      `;
    }).join('');

    const html = `
<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">

<title>Nota de compra</title>

<style>

@page {
  size: A4;
  margin: 14mm;
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  background: #fff;
  color: #1e293b;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 12px;
}

.document {
  width: 100%;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 2px solid #1e293b;
  padding-bottom: 12px;
  margin-bottom: 18px;
}

.company {
  font-size: 21px;
  font-weight: 700;
}

.company-subtitle {
  margin-top: 4px;
  color: #64748b;
  font-size: 10px;
}

.title {
  text-align: right;
}

.title h1 {
  margin: 0;
  font-size: 18px;
  text-transform: uppercase;
}

.title div {
  margin-top: 5px;
  color: #64748b;
}

.info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 18px;
}

.box {
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 11px;
}

.box-title {
  margin-bottom: 8px;
  color: #64748b;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
}

.row {
  display: flex;
  margin-bottom: 5px;
}

.label {
  width: 105px;
  color: #64748b;
}

.value {
  flex: 1;
  font-weight: 600;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

th {
  padding: 8px 6px;
  border: 1px solid #cbd5e1;
  background: #f1f5f9;
  text-align: left;
  font-size: 9px;
  text-transform: uppercase;
}

td {
  padding: 8px 6px;
  border: 1px solid #cbd5e1;
  vertical-align: top;
}

.center {
  text-align: center;
}

.money {
  text-align: right;
  white-space: nowrap;
}

.total td {
  background: #f8fafc;
  font-weight: bold;
  font-size: 13px;
}

.notes {
  margin-top: 18px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 11px;
  min-height: 65px;
}

.notes-title {
  color: #64748b;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 7px;
}

.signatures {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 70px;
  margin-top: 60px;
}

.signature {
  border-top: 1px solid #64748b;
  text-align: center;
  padding-top: 7px;
}

.footer {
  margin-top: 25px;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
  text-align: center;
  color: #94a3b8;
  font-size: 9px;
}

@media print {
  body {
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
}


        /* =========================================================
           HISTORIAL DE COMPRAS - DISEÑO RESPONSIVE
           ========================================================= */

        /* =====================================================
           HISTORIAL DE COMPRAS - TARJETAS MOVILES
           ===================================================== */

        .compras-history-mobile {
          width: 100%;
          min-width: 0;
        }

        .compras-history-mobile * {
          box-sizing: border-box;
        }

        .compras-history-mobile button {
          min-height: 46px;
        }

        .compras-history-mobile .break-words {
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .compras-history-mobile .purchase-card {
          width: 100%;
          min-width: 0;
          overflow: hidden;
        }

        .compras-history-mobile .purchase-card-value {
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        /* =====================================================
           HISTORIAL RESPONSIVE DEFINITIVO
           0-767px  = tarjetas
           768px+   = tabla
           ===================================================== */

        .compras-history-desktop {
          width: 100%;
          min-width: 0;
          max-width: 100%;
        }

        .compras-history-desktop > table {
          width: 100%;
          max-width: 100%;
        }

        .compras-history-mobile {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow: hidden;
        }

        .compras-history-mobile .purchase-card {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow: hidden;
        }

        .compras-history-mobile .purchase-card-value {
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        /* Hasta 767px: JAMAS mostrar la tabla */
        @media (max-width: 767px) {

          .compras-history-desktop {
            display: none !important;
            width: 0 !important;
            max-width: 0 !important;
            overflow: hidden !important;
          }

          .compras-history-mobile {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            overflow: hidden !important;
          }

          .compras-history-mobile .purchase-card {
            width: 100% !important;
            max-width: 100% !important;
          }
        }

        /* Desde 768px: tabla, nunca tarjetas */
        @media (min-width: 768px) {

          .compras-history-desktop {
            display: block !important;
          }

          .compras-history-mobile {
            display: none !important;
          }
        }
</style>
</head>

<body>

<div class="document">

  <div class="header">

    <div>
      <div class="company">MAQCONTROL</div>

      <div class="company-subtitle">
        Control de maquinaria, compras y mantenimiento
      </div>
    </div>

    <div class="title">

      <h1>Nota de compra</h1>

      <div>
        Fecha:
        ${escapeHtml(formatDate(purchase.purchase_date))}
      </div>

    </div>

  </div>


  <div class="info">

    <div class="box">

      <div class="box-title">
        Proveedor
      </div>

      <div class="row">
        <div class="label">Nombre:</div>
        <div class="value">
          ${escapeHtml(
            purchase.supplier_name ||
            supplier?.name ||
            'Sin proveedor'
          )}
        </div>
      </div>

      <div class="row">
        <div class="label">NIT:</div>
        <div class="value">
          ${escapeHtml(supplier?.nit || '—')}
        </div>
      </div>

      <div class="row">
        <div class="label">Contacto:</div>
        <div class="value">
          ${escapeHtml(supplier?.phone || '—')}
        </div>
      </div>

      <div class="row">
        <div class="label">Dirección:</div>
        <div class="value">
          ${escapeHtml(supplier?.address || '—')}
        </div>
      </div>

    </div>


    <div class="box">

      <div class="box-title">
        Datos de la compra
      </div>

      <div class="row">
        <div class="label">Factura:</div>
        <div class="value">
          ${escapeHtml(purchase.invoice_number || '—')}
        </div>
      </div>

      <div class="row">
        <div class="label">Forma de pago:</div>
        <div class="value">
          ${escapeHtml(purchase.payment_method || '—')}
        </div>
      </div>

      <div class="row">
        <div class="label">Comprador:</div>
        <div class="value">
          ${escapeHtml(purchase.buyer_name || '—')}
        </div>
      </div>

      <div class="row">
        <div class="label">Pagado por:</div>
        <div class="value">
          ${escapeHtml(purchase.paid_by_name || '—')}
        </div>
      </div>

      <div class="row">
        <div class="label">Registrado por:</div>
        <div class="value">
          ${escapeHtml(purchase.created_by_name || '—')}
        </div>
      </div>

    </div>

  </div>


  <table>

    <thead>

      <tr>
        <th>Producto</th>
        <th>Categoría</th>
        <th style="text-align:center">Cant.</th>
        <th>Unidad</th>
        <th style="text-align:right">P. Unitario</th>
        <th style="text-align:right">Subtotal</th>
      </tr>

    </thead>

    <tbody>

      ${itemsHtml}

      <tr class="total">

        <td colspan="5" style="text-align:right">
          TOTAL
        </td>

        <td class="money">
          Bs ${formatMoney(purchase.total)}
        </td>

      </tr>

    </tbody>

  </table>


  <div class="notes">

    <div class="notes-title">
      Observaciones
    </div>

    <div>
      ${escapeHtml(
        purchase.notes ||
        'Sin observaciones'
      )}
    </div>

  </div>


  <div class="signatures">

    <div class="signature">
      Proveedor
    </div>

    <div class="signature">
      Responsable de compra
    </div>

  </div>


  <div class="footer">
    Documento generado desde MaqControl · Historial de Compras
  </div>

</div>


<script>

window.onload = function() {
  setTimeout(function() {
    window.print();
  }, 300);
};

</script>

</body>
</html>
`;

    const printWindow = window.open(
      '',
      '_blank',
      'width=900,height=700'
    );

    if (!printWindow) {
      alert(
        'El navegador bloqueó la ventana de impresión. ' +
        'Permite ventanas emergentes para MaqControl.'
      );
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  
    const printReport = () => {
      const escapeHtmlReport = (value) => {
        return String(value ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      };

      const formatReportMoney = (value) => {
        return `Bs ${Number(value || 0).toLocaleString('es-BO', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`;
      };

      const formatReportDate = (value) => {
        if (!value) return '—';

        const parts = String(value).split('-');

        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }

        return value;
      };

      const productName = reportFilters.product_id
        ? (
            products.find(
              product =>
                String(product.id) === String(reportFilters.product_id)
            )?.name || 'Producto seleccionado'
          )
        : 'Todos los productos';

      const supplierName = reportFilters.supplier_id
        ? (
            suppliers.find(
              supplier =>
                String(supplier.id) === String(reportFilters.supplier_id)
            )?.name || 'Proveedor seleccionado'
          )
        : 'Todos los proveedores';

      const buyerName = reportFilters.buyer_id
        ? (
            users.find(
              user =>
                String(user.id) === String(reportFilters.buyer_id)
            )?.name || 'Persona seleccionada'
          )
        : 'Todas las personas';

      const paidByName = reportFilters.paid_by_id
        ? (
            users.find(
              user =>
                String(user.id) === String(reportFilters.paid_by_id)
            )?.name || 'Persona seleccionada'
          )
        : 'Todas las personas';

      const paymentName =
        reportFilters.payment_method || 'Todas las formas de pago';

      const dateFrom = reportFilters.date_from
        ? formatReportDate(reportFilters.date_from)
        : 'Inicio';

      const dateTo = reportFilters.date_to
        ? formatReportDate(reportFilters.date_to)
        : 'Hasta';

      const generatedAt = new Date().toLocaleString('es-BO', {
        dateStyle: 'short',
        timeStyle: 'short'
      });

      const rowsHtml = reportData.rows.length
        ? reportData.rows.map(row => `
            <tr>
              <td>
                ${escapeHtmlReport(
                  formatReportDate(row.purchase_date)
                )}
              </td>

              <td>
                <strong>
                  ${escapeHtmlReport(row.product_name)}
                </strong>

                ${
                  row.category
                    ? `<div class="muted">${escapeHtmlReport(row.category)}</div>`
                    : ''
                }
              </td>

              <td>
                ${escapeHtmlReport(row.supplier_name)}
              </td>

              <td>
                ${escapeHtmlReport(row.invoice_number)}
              </td>

              <td class="right">
                ${escapeHtmlReport(row.quantity)}
                ${escapeHtmlReport(row.unit)}
              </td>

              <td class="right">
                ${formatReportMoney(row.unit_price)}
              </td>

              <td class="right strong">
                ${formatReportMoney(row.total)}
              </td>
            </tr>
          `).join('')
        : `
            <tr>
              <td colspan="7" class="empty">
                No hay compras que coincidan con los filtros seleccionados.
              </td>
            </tr>
          `;

      const html = `
<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">

<title>Reporte de Compras - MaqControl</title>

<style>

@page {
  size: A4 landscape;
  margin: 12mm;
}

* {
  box-sizing: border-box;
}

body {
  font-family: Arial, Helvetica, sans-serif;
  color: #1e293b;
  margin: 0;
  font-size: 10px;
  background: white;
}

.header {
  border-bottom: 2px solid #1e293b;
  padding-bottom: 10px;
  margin-bottom: 12px;
}

.company {
  font-size: 20px;
  font-weight: 700;
}

.title {
  font-size: 16px;
  font-weight: 700;
  margin-top: 4px;
}

.subtitle {
  color: #64748b;
  margin-top: 3px;
}

.period {
  margin-top: 8px;
  font-size: 11px;
  font-weight: 600;
}

.filters {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px 18px;
  border: 1px solid #cbd5e1;
  padding: 9px;
  margin-bottom: 12px;
  background: #f8fafc;
}

.filter-label {
  color: #64748b;
  font-size: 8px;
  text-transform: uppercase;
  margin-bottom: 2px;
}

.filter-value {
  font-weight: 600;
}

.summary {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
  margin-bottom: 14px;
}

.summary-card {
  border: 1px solid #cbd5e1;
  padding: 8px;
  min-height: 48px;
}

.summary-label {
  color: #64748b;
  font-size: 8px;
  text-transform: uppercase;
}

.summary-value {
  font-size: 13px;
  font-weight: 700;
  margin-top: 3px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  display: table-header-group;
}

tr {
  page-break-inside: avoid;
}

th {
  background: #e2e8f0;
  border: 1px solid #cbd5e1;
  padding: 6px;
  text-align: left;
  font-size: 8px;
  text-transform: uppercase;
}

td {
  border: 1px solid #cbd5e1;
  padding: 6px;
  vertical-align: top;
}

.right {
  text-align: right;
  white-space: nowrap;
}

.strong {
  font-weight: 700;
}

.muted {
  color: #64748b;
  font-size: 8px;
  margin-top: 2px;
}

.empty {
  text-align: center;
  padding: 25px;
  color: #64748b;
}

.footer {
  margin-top: 14px;
  padding-top: 7px;
  border-top: 1px solid #cbd5e1;
  display: flex;
  justify-content: space-between;
  color: #64748b;
  font-size: 8px;
}

@media print {
  body {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}

</style>
</head>

<body>

<div class="header">

  <div class="company">
    MAQCONTROL
  </div>

  <div class="title">
    REPORTE DE COMPRAS
  </div>

  <div class="subtitle">
    Historial y análisis de compras
  </div>

  <div class="period">
    Período:
    ${escapeHtmlReport(dateFrom)}
    —
    ${escapeHtmlReport(dateTo)}
  </div>

</div>

<div class="filters">

  <div>
    <div class="filter-label">Producto</div>
    <div class="filter-value">
      ${escapeHtmlReport(productName)}
    </div>
  </div>

  <div>
    <div class="filter-label">Proveedor</div>
    <div class="filter-value">
      ${escapeHtmlReport(supplierName)}
    </div>
  </div>

  <div>
    <div class="filter-label">Comprado por</div>
    <div class="filter-value">
      ${escapeHtmlReport(buyerName)}
    </div>
  </div>

  <div>
    <div class="filter-label">Pagado por</div>
    <div class="filter-value">
      ${escapeHtmlReport(paidByName)}
    </div>
  </div>

  <div>
    <div class="filter-label">Forma de pago</div>
    <div class="filter-value">
      ${escapeHtmlReport(paymentName)}
    </div>
  </div>

  <div>
    <div class="filter-label">Registros</div>
    <div class="filter-value">
      ${reportData.rows.length}
    </div>
  </div>

</div>

<div class="summary">

  <div class="summary-card">
    <div class="summary-label">Compras</div>
    <div class="summary-value">
      ${reportData.purchase_count}
    </div>
  </div>

  <div class="summary-card">
    <div class="summary-label">Cantidad</div>
    <div class="summary-value">
      ${reportData.quantity}
    </div>
  </div>

  <div class="summary-card">
    <div class="summary-label">Total comprado</div>
    <div class="summary-value">
      ${formatReportMoney(reportData.total)}
    </div>
  </div>

  <div class="summary-card">
    <div class="summary-label">Precio promedio</div>
    <div class="summary-value">
      ${formatReportMoney(reportData.average_unit_price)}
    </div>
  </div>

  <div class="summary-card">
    <div class="summary-label">Precio mínimo</div>
    <div class="summary-value">
      ${formatReportMoney(reportData.min_unit_price)}
    </div>
  </div>

  <div class="summary-card">
    <div class="summary-label">Precio máximo</div>
    <div class="summary-value">
      ${formatReportMoney(reportData.max_unit_price)}
    </div>
  </div>

</div>

<table>

<thead>
<tr>
  <th>Fecha</th>
  <th>Producto</th>
  <th>Proveedor</th>
  <th>Factura</th>
  <th style="text-align:right">Cantidad</th>
  <th style="text-align:right">P. Unitario</th>
  <th style="text-align:right">Total</th>
</tr>
</thead>

<tbody>
${rowsHtml}
</tbody>

</table>

<div class="footer">

  <div>
    Documento generado desde MaqControl
  </div>

  <div>
    Generado:
    ${escapeHtmlReport(generatedAt)}
  </div>

</div>

<script>

window.onload = function() {
  setTimeout(function() {
    window.print();
  }, 300);
};

</script>

</body>
</html>
      `;

      const printWindow = window.open(
        '',
        '_blank',
        'width=1200,height=800'
      );

      if (!printWindow) {
        alert(
          'El navegador bloqueó la ventana de impresión. ' +
          'Permite ventanas emergentes para MaqControl.'
        );
        return;
      }

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    };

return (
    <div className="compras-mobile p-3 sm:p-6 space-y-4 sm:space-y-6">

      <style>{`
        .compras-mobile {
          -webkit-tap-highlight-color: transparent;
          min-width: 0;
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
        }

        .compras-mobile .compras-tabs {
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          min-width: 0;
        }

        .compras-mobile .compras-tabs::-webkit-scrollbar {
          display: none;
        }

        .compras-mobile .compras-tabs > button {
          flex: 0 0 auto;
          min-height: 46px;
          white-space: nowrap;
          padding-left: 14px;
          padding-right: 14px;
        }

        .compras-mobile input,
        .compras-mobile select,
        .compras-mobile textarea {
          min-height: 44px;
          max-width: 100%;
          box-sizing: border-box;
        }

        .compras-mobile button {
          min-height: 44px;
          max-width: 100%;
        }

        .compras-mobile .overflow-x-auto {
          -webkit-overflow-scrolling: touch;
          overscroll-behavior-x: contain;
          max-width: 100%;
        }

        .compras-mobile table {
          width: 100%;
          max-width: 100%;
        }

        @media (max-width: 640px) {

          .compras-mobile {
            width: 100%;
            max-width: 100vw;
            padding-left: 12px;
            padding-right: 12px;
            overflow-x: hidden;
          }

          .compras-mobile h1 {
            font-size: 1.35rem;
            line-height: 1.2;
          }

          .compras-mobile h2 {
            font-size: 1.15rem;
          }

          .compras-mobile .grid,
          .compras-mobile .flex {
            min-width: 0;
          }

          .compras-mobile .grid > *,
          .compras-mobile .flex > * {
            min-width: 0;
            max-width: 100%;
          }

          .compras-mobile .grid > div .text-2xl {
            font-size: 1.25rem;
          }

          /* Tablas: nunca imponer 680px en celular */
          .compras-mobile table {
            min-width: 0 !important;
            width: 100% !important;
            table-layout: fixed;
          }

          .compras-mobile th,
          .compras-mobile td {
            min-width: 0 !important;
            max-width: 100%;
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          /* Si una tabla realmente necesita desplazamiento,
             conserva el contenedor y no rompe toda la pantalla */
          .compras-mobile .overflow-x-auto {
            width: 100%;
            max-width: 100%;
            overflow-x: auto;
          }

          /* Modales tipo bottom-sheet */
          .compras-mobile .fixed.inset-0 {
            align-items: flex-end;
            justify-content: center;
            padding: 0;
            width: 100vw;
            max-width: 100vw;
            overflow: hidden;
          }

          .compras-mobile .fixed.inset-0 > form {
            width: 100%;
            max-width: 100%;
            max-height: 94vh;
            margin: 0;
            padding: 16px !important;
            border-radius: 18px 18px 0 0;
            overflow-y: auto;
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
            box-sizing: border-box;
          }

          /* Todos los campos ocupan el ancho disponible */
          .compras-mobile .fixed.inset-0 form label,
          .compras-mobile .fixed.inset-0 form input,
          .compras-mobile .fixed.inset-0 form select,
          .compras-mobile .fixed.inset-0 form textarea {
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
          }

          .compras-mobile input,
          .compras-mobile select,
          .compras-mobile textarea {
            font-size: 16px;
          }

          /* Botones finales de formularios */
          .compras-mobile .fixed.inset-0 form .justify-end {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
            width: 100%;
          }

          .compras-mobile .fixed.inset-0 form .justify-end > button {
            width: 100%;
          }

          /* Botones de cabecera: nunca desbordar */
          .compras-mobile > .flex:first-of-type {
            min-width: 0;
            width: 100%;
          }

          /* Evitar que textos largos rompan tarjetas */
          .compras-mobile .truncate {
            min-width: 0;
          }

          /* Inputs de búsqueda */
          .compras-mobile input[type="search"] {
            width: 100%;
          }
        }
      `}</style>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Compras</h1>
          <p className="text-slate-500">
            Registro de compras, catálogo de productos y proveedores
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowSupplier(true)}
            className="px-3 py-2 border rounded-lg bg-white"
          >
            <Users size={17} className="inline mr-1" /> Proveedor
          </button>
          <button
            onClick={() => setShowProduct(true)}
            className="px-3 py-2 border rounded-lg bg-white"
          >
            <Package size={17} className="inline mr-1" /> Producto
          </button>
          <label
            className={`px-3 py-2 border rounded-lg cursor-pointer flex items-center justify-center gap-1 ${
              aiLoading
                ? 'bg-slate-100 text-slate-400 cursor-wait'
                : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
            title="Importar factura o recibo con IA"
          >
            {aiLoading ? (
              <>
                <Loader2 size={17} className="animate-spin" />
                Analizando...
              </>
            ) : (
              <>
                <Sparkles size={17} />
                Importar con IA
              </>
            )}

            <input
              key={aiFileInputKey}
              type="file"
              className="hidden"
              accept="image/*,.pdf,application/pdf"
              capture="environment"
              disabled={aiLoading}
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) importPurchaseWithAI(file);
              }}
            />
          </label>

          <button
            onClick={() => {
              setEditingPurchaseId(null);
              setError('');
              setPurchase({
                purchase_date: new Date().toISOString().slice(0, 10),
                supplier_id: '',
                supplier_name: '',
                supplier_nit: '',
                supplier_phone: '',
                supplier_whatsapp: '',
                supplier_contact: '',
                supplier_address: '',
                supplier_notes: '',
                invoice_number: '',
                payment_method: '',
                buyer_id: '',
                buyer_name: '',
                paid_by_id: '',
                paid_by_name: '',
                request_id: '',
                notes: '',
                items: [emptyItem()]
              });
              setShowPurchase(true);
            }}
            className="px-4 py-2 rounded-lg bg-slate-800 text-white"
          >
            <Plus size={17} className="inline mr-1" /> Nueva compra
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <div className="bg-white border rounded-xl p-4">
          <div className="text-slate-500 text-sm">Compras registradas</div>
          <div className="text-2xl font-bold">{summary?.purchase_count || 0}</div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-slate-500 text-sm">Compras este mes</div>
          <div className="text-2xl font-bold">{summary?.month_purchase_count || 0}</div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-slate-500 text-sm">Total del mes</div>
          <div className="text-2xl font-bold">{money(summary?.month_total)}</div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-slate-500 text-sm">Productos / Proveedores</div>
          <div className="text-2xl font-bold">
            {summary?.product_count || 0} / {summary?.supplier_count || 0}
          </div>
        </div>
      </div>

      <div className="flex border-b overflow-x-auto compras-tabs">
        {[
          ['compras', <Receipt size={16} />, 'Historial de compras'],
          ['reportes', <TrendingUp size={16} />, 'Reportes'],
          ['cotizador', <Receipt size={16} />, 'Cotizador'],
          ['productos', <Package size={16} />, 'Catálogo de productos'],
          ['proveedores', <Users size={16} />, 'Proveedores']
        ].map(([key, icon, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-3 flex items-center gap-2 border-b-2 ${
              tab === key
                ? 'border-slate-800 text-slate-800'
                : 'border-transparent text-slate-500'
            }`}
          >
            {icon}{label}
          </button>
        ))}
      </div>

      {tab === 'compras' && (
        <div className="bg-white border rounded-xl overflow-hidden">

          <div className="p-4 border-b space-y-3">

            <div className="flex justify-between items-center">
              <div>
                <div className="font-semibold">Historial de compras</div>
                <div className="text-xs text-slate-500">
                  {filteredPurchases.length} de {purchases.length} compras
                </div>
              </div>

              <button
                onClick={load}
                className="p-2 border rounded-lg hover:bg-slate-50"
                title="Actualizar"
              >
                <RefreshCw size={16} />
              </button>
            </div>

            <div className="relative">
              <Search
                size={17}
                className="absolute left-3 top-3 text-slate-400"
              />

              <input
                value={purchaseSearch}
                onChange={e => setPurchaseSearch(e.target.value)}
                placeholder="Buscar producto, proveedor, factura, comprador, pagado por..."
                className="w-full border rounded-lg pl-10 pr-10 py-2"
              />

              {purchaseSearch && (
                <button
                  type="button"
                  onClick={() => setPurchaseSearch('')}
                  className="absolute right-2 top-2 p-1 rounded hover:bg-slate-100 text-slate-500"
                  title="Limpiar búsqueda"
                >
                  <X size={17} />
                </button>
              )}
            </div>

          </div>

          
<div className="compras-history-desktop hidden md:block overflow-x-auto">
<div className="overflow-x-auto">
            <table className="w-full text-sm">

              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 whitespace-nowrap">
                    Fecha
                  </th>

                  <th className="text-left p-3">
                    Producto / detalle
                  </th>

                  <th className="text-left p-3">
                    Proveedor
                  </th>

                  <th className="text-left p-3">
                    Factura
                  </th>

                  <th className="text-right p-3 whitespace-nowrap">
                    Total
                  </th>

                  <th className="text-right p-3">
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody>

                {filteredPurchases.map(p => (
                  <tr
                    key={p.id}
                    className="border-t hover:bg-slate-50"
                  >

                    <td className="p-3 align-top whitespace-nowrap">
                      <div className="font-medium">
                        {formatPurchaseDate(p.purchase_date)}
                      </div>
                    </td>

                    <td className="p-3 align-top min-w-[300px]">
                      <div className="font-medium text-slate-800">
                        {getPurchaseItemsPreview(p)}
                      </div>

                      {p.items?.length > 2 && (
                        <div className="text-xs text-slate-500 mt-1">
                          {p.items.length} productos en total
                        </div>
                      )}
                    </td>

                    <td className="p-3 align-top">
                      <div className="font-medium">
                        {p.supplier_name || 'Sin proveedor'}
                      </div>
                    </td>

                    <td className="p-3 align-top whitespace-nowrap">
                      {p.invoice_number || '—'}
                    </td>

                    <td className="p-3 align-top text-right font-semibold whitespace-nowrap">
                      {money(p.total)}
                    </td>

                    <td className="p-3 align-top text-right">
                      <div className="flex justify-end gap-1">

                        <button
                          onClick={() => viewPurchase(p)}
                          className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg"
                          title="Ver detalle"
                        >
                          <Eye size={16} />
                        </button>

                        <button
                          onClick={() => editPurchase(p)}
                          className="text-amber-600 hover:bg-amber-50 p-2 rounded-lg"
                          title="Editar compra"
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          onClick={() => printPurchase(p)}
                          className="text-slate-600 hover:bg-slate-100 p-2 rounded-lg"
                          title="Imprimir / PDF"
                        >
                          <Printer size={16} />
                        </button>

                        <button
                          onClick={() => deletePurchase(p.id)}
                          className="text-red-600 hover:bg-red-50 p-2 rounded-lg"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>

                      </div>
                    </td>

                  </tr>
                ))}

                {!filteredPurchases.length && !loading && (
                  <tr>
                    <td
                      colSpan="6"
                      className="p-8 text-center text-slate-500"
                    >
                      {purchaseSearch
                        ? `No se encontraron compras para "${purchaseSearch}".`
                        : 'Todavía no hay compras registradas.'}
                    </td>
                  </tr>
                )}

              </tbody>

            </table>
          </div>
</div>


<div className="compras-history-mobile block md:hidden space-y-3">
  {filteredPurchases.map((p) => (
    <div
      key={p.id}
      className="purchase-card bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden"
    >
      <div className="p-4">

        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              Fecha de compra
            </div>

            <div className="text-base font-bold text-slate-800 mt-1">
              {p.purchase_date || 'Sin fecha'}
            </div>
          </div>

          <div className="text-right shrink-0">
            <div className="text-xs text-slate-400">
              Total
            </div>

            <div className="text-lg font-bold text-slate-900 whitespace-nowrap">
              {money(p.total)}
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-2">

          <div>
            <div className="text-xs text-slate-400">
              Proveedor
            </div>

            <div className="font-semibold text-slate-800 break-words">
              {p.supplier_name || 'Sin proveedor'}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">

            <div className="min-w-0">
              <div className="text-xs text-slate-400">
                Factura
              </div>

              <div className="text-sm font-medium text-slate-700 break-words">
                {p.invoice_number || '—'}
              </div>
            </div>

            <div className="min-w-0">
              <div className="text-xs text-slate-400">
                Productos
              </div>

              <div className="text-sm font-medium text-slate-700">
                {p.items?.length || 0}
              </div>
            </div>

          </div>

          {(p.buyer_name || p.paid_by_name) && (
            <div className="grid grid-cols-1 gap-2 pt-1">

              {p.buyer_name && (
                <div>
                  <div className="text-xs text-slate-400">
                    Responsable de compra
                  </div>

                  <div className="text-sm text-slate-700 break-words">
                    {p.buyer_name}
                  </div>
                </div>
              )}

              {p.paid_by_name && (
                <div>
                  <div className="text-xs text-slate-400">
                    Pagado por
                  </div>

                  <div className="text-sm text-slate-700 break-words">
                    {p.paid_by_name}
                  </div>
                </div>
              )}

            </div>
          )}

          {(p.payment_method || p.request_id) && (
            <div className="grid grid-cols-2 gap-3 pt-1">

              {p.payment_method && (
                <div className="min-w-0">
                  <div className="text-xs text-slate-400">
                    Pago
                  </div>

                  <div className="text-sm font-medium text-slate-700 break-words">
                    {p.payment_method}
                  </div>
                </div>
              )}

              {p.request_id && (
                <div className="min-w-0">
                  <div className="text-xs text-slate-400">
                    Pedido
                  </div>

                  <div className="text-sm font-medium text-slate-700 break-words">
                    {requests.find(r => String(r.id) === String(p.request_id))?.order_number ||
                      p.request_id}
                  </div>
                </div>
              )}

            </div>
          )}

        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">

          <button
            type="button"
            onClick={() => viewPurchase(p)}
            className="min-h-[46px] rounded-xl border border-blue-200 bg-blue-50 text-blue-700 flex items-center justify-center gap-1.5 font-medium active:scale-[0.98]"
          >
            <Eye size={17} />
            <span>Ver</span>
          </button>

          <button
            type="button"
            onClick={() => editPurchase(p)}
            className="min-h-[46px] rounded-xl border border-amber-200 bg-amber-50 text-amber-700 flex items-center justify-center gap-1.5 font-medium active:scale-[0.98]"
          >
            <Pencil size={17} />
            <span>Editar</span>
          </button>

          <button
            type="button"
            onClick={() => printPurchase(p)}
            className="min-h-[46px] rounded-xl border border-slate-200 bg-slate-50 text-slate-700 flex items-center justify-center gap-1.5 font-medium active:scale-[0.98]"
          >
            <Printer size={17} />
            <span>PDF</span>
          </button>

          <button
            type="button"
            onClick={() => deletePurchase(p.id)}
            className="min-h-[46px] rounded-xl border border-red-200 bg-red-50 text-red-700 flex items-center justify-center gap-1.5 font-medium active:scale-[0.98]"
          >
            <Trash2 size={17} />
            <span>Eliminar</span>
          </button>

        </div>

      </div>
    </div>
  ))}

  {!purchases.length && !loading && (
    <div className="bg-white border rounded-2xl p-8 text-center text-slate-500">
      Todavía no hay compras registradas.
    </div>
  )}

  {purchases.length > 0 && !filteredPurchases.length && (
    <div className="bg-white border rounded-2xl p-8 text-center text-slate-500">
      No se encontraron compras con ese criterio de búsqueda.
    </div>
  )}
</div>


        </div>
      )}

      {selectedPurchase && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4">

          <div className="purchase-detail-modal bg-white w-full md:max-w-5xl md:rounded-2xl rounded-t-3xl shadow-2xl max-h-[94vh] md:max-h-[90vh] overflow-hidden">

            {/* CABECERA */}
            <div className="flex items-center justify-between gap-3 p-4 md:p-5 border-b bg-slate-50">

              <div className="min-w-0">
                <h2 className="text-lg md:text-xl font-bold text-slate-800">
                  Detalle de compra
                </h2>

                <p className="text-xs md:text-sm text-slate-500 mt-1">
                  Información completa de la compra
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedPurchase(null)}
                className="shrink-0 w-10 h-10 rounded-xl hover:bg-slate-200 text-slate-600 flex items-center justify-center"
                title="Cerrar"
              >
                <X size={20} />
              </button>

            </div>


            {/* CONTENIDO CON SCROLL */}
            <div className="purchase-detail-scroll p-4 md:p-6 overflow-y-auto overflow-x-hidden max-h-[calc(94vh-73px)] md:max-h-[calc(90vh-80px)] pb-32 md:pb-6">

              {/* RESUMEN */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">

                <div className="border rounded-xl p-3 md:p-4 min-w-0">
                  <div className="text-[10px] md:text-xs text-slate-400 uppercase">
                    Fecha
                  </div>

                  <div className="font-semibold mt-1 text-slate-800 break-words">
                    {selectedPurchase.purchase_date
                      ? String(selectedPurchase.purchase_date).split('-').reverse().join('/')
                      : '—'}
                  </div>
                </div>


                <div className="border rounded-xl p-3 md:p-4 min-w-0">
                  <div className="text-[10px] md:text-xs text-slate-400 uppercase">
                    Factura
                  </div>

                  <div className="font-semibold mt-1 text-slate-800 break-words">
                    {selectedPurchase.invoice_number || '—'}
                  </div>
                </div>


                <div className="border rounded-xl p-3 md:p-4 min-w-0">
                  <div className="text-[10px] md:text-xs text-slate-400 uppercase">
                    Forma de pago
                  </div>

                  <div className="font-semibold mt-1 text-slate-800 break-words">
                    {selectedPurchase.payment_method || '—'}
                  </div>
                </div>


                <div className="col-span-2 md:col-span-1 rounded-xl p-3 md:p-4 bg-slate-800 text-white min-w-0">

                  <div className="text-[10px] md:text-xs text-slate-300 uppercase">
                    Total
                  </div>

                  <div className="text-xl md:text-2xl font-bold mt-1 whitespace-nowrap">
                    {money(selectedPurchase.total)}
                  </div>

                </div>

              </div>


              {/* PROVEEDOR */}
              <div className="border rounded-2xl p-4 md:p-5 mb-5">

                <h3 className="font-semibold text-slate-800 mb-4">
                  Proveedor
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                  <div className="min-w-0">
                    <div className="text-xs text-slate-400">
                      Nombre
                    </div>

                    <div className="font-medium mt-1 break-words">
                      {selectedPurchase.supplier_name || 'Sin proveedor'}
                    </div>
                  </div>


                  <div className="min-w-0">
                    <div className="text-xs text-slate-400">
                      NIT
                    </div>

                    <div className="font-medium mt-1 break-words">
                      {suppliers.find(
                        s => String(s.id) === String(selectedPurchase.supplier_id)
                      )?.nit || selectedPurchase.supplier_nit || '—'}
                    </div>
                  </div>


                  <div className="min-w-0">
                    <div className="text-xs text-slate-400">
                      Contacto
                    </div>

                    <div className="font-medium mt-1 break-words">
                      {suppliers.find(
                        s => String(s.id) === String(selectedPurchase.supplier_id)
                      )?.phone || selectedPurchase.supplier_phone || '—'}
                    </div>
                  </div>


                  <div className="min-w-0">
                    <div className="text-xs text-slate-400">
                      Dirección
                    </div>

                    <div className="font-medium mt-1 break-words">
                      {suppliers.find(
                        s => String(s.id) === String(selectedPurchase.supplier_id)
                      )?.address || selectedPurchase.supplier_address || '—'}
                    </div>
                  </div>

                </div>

              </div>


              {/* PEDIDO RELACIONADO */}
              <div className="border rounded-2xl p-4 md:p-5 mb-5">

                <div className="text-xs text-slate-400 uppercase">
                  Pedido relacionado
                </div>

                <div className="font-semibold text-slate-800 mt-1 break-words">
                  {requests.find(
                    r => String(r.id) === String(selectedPurchase.request_id)
                  )?.order_number || (
                    selectedPurchase.request_id
                      ? 'Pedido relacionado'
                      : 'Sin pedido relacionado'
                  )}
                </div>

              </div>


              {/* PRODUCTOS */}
              <div className="border rounded-2xl overflow-hidden mb-5">

                <div className="p-4 bg-slate-50 border-b flex items-center justify-between gap-3">

                  <h3 className="font-semibold text-slate-800">
                    Productos comprados
                  </h3>

                  <span className="text-xs text-slate-500 whitespace-nowrap">
                    {selectedPurchase.items?.length || 0} producto(s)
                  </span>

                </div>


                {/* ESCRITORIO */}
                <div className="hidden md:block overflow-x-auto">

                  <table className="w-full text-sm table-fixed">

                    <thead className="bg-slate-50">

                      <tr>

                        <th className="text-left p-3 w-[34%]">
                          Producto
                        </th>

                        <th className="text-left p-3 w-[16%]">
                          Categoría
                        </th>

                        <th className="text-center p-3 w-[10%]">
                          Cantidad
                        </th>

                        <th className="text-left p-3 w-[12%]">
                          Unidad
                        </th>

                        <th className="text-right p-3 w-[14%]">
                          Precio unit.
                        </th>

                        <th className="text-right p-3 w-[14%]">
                          Subtotal
                        </th>

                      </tr>

                    </thead>


                    <tbody>

                      {(selectedPurchase.items || []).map((item, index) => (

                        <tr key={index} className="border-t">

                          <td className="p-3 font-medium text-slate-800 break-words">
                            {item.product_name || '—'}
                          </td>

                          <td className="p-3 text-slate-600 break-words">
                            {item.category || '—'}
                          </td>

                          <td className="p-3 text-center">
                            {item.quantity ?? 0}
                          </td>

                          <td className="p-3 text-slate-600 break-words">
                            {item.unit || '—'}
                          </td>

                          <td className="p-3 text-right whitespace-nowrap">
                            {money(item.unit_price)}
                          </td>

                          <td className="p-3 text-right font-semibold whitespace-nowrap">
                            {money(
                              Number(item.quantity || 0) *
                              Number(item.unit_price || 0)
                            )}
                          </td>

                        </tr>

                      ))}

                    </tbody>


                    <tfoot>

                      <tr className="border-t bg-slate-50">

                        <td
                          colSpan="5"
                          className="p-4 text-right font-bold"
                        >
                          TOTAL
                        </td>

                        <td className="p-4 text-right font-bold text-lg whitespace-nowrap">
                          {money(selectedPurchase.total)}
                        </td>

                      </tr>

                    </tfoot>

                  </table>

                </div>


                {/* MOVIL */}
                <div className="md:hidden p-3 space-y-3">

                  {(selectedPurchase.items || []).map((item, index) => (

                    <div
                      key={index}
                      className="border border-slate-200 rounded-2xl overflow-hidden bg-white"
                    >

                      <div className="p-4">

                        <div className="text-[10px] text-slate-400 uppercase mb-1">
                          Producto {index + 1}
                        </div>

                        <div className="font-bold text-slate-800 leading-snug break-words">
                          {item.product_name || 'Producto sin nombre'}
                        </div>

                        {item.category && (
                          <div className="text-xs text-slate-500 mt-1 break-words">
                            {item.category}
                          </div>
                        )}

                      </div>


                      <div className="grid grid-cols-2 gap-3 p-4 bg-slate-50 border-t">

                        <div>
                          <div className="text-[10px] text-slate-400 uppercase">
                            Cantidad
                          </div>

                          <div className="font-semibold mt-1">
                            {item.quantity ?? 0}
                          </div>
                        </div>


                        <div>
                          <div className="text-[10px] text-slate-400 uppercase">
                            Unidad
                          </div>

                          <div className="font-semibold mt-1 break-words">
                            {item.unit || '—'}
                          </div>
                        </div>


                        <div>
                          <div className="text-[10px] text-slate-400 uppercase">
                            Precio unitario
                          </div>

                          <div className="font-semibold mt-1 whitespace-nowrap">
                            {money(item.unit_price)}
                          </div>
                        </div>


                        <div>
                          <div className="text-[10px] text-slate-400 uppercase">
                            Subtotal
                          </div>

                          <div className="font-bold mt-1 whitespace-nowrap">
                            {money(
                              Number(item.quantity || 0) *
                              Number(item.unit_price || 0)
                            )}
                          </div>
                        </div>

                      </div>

                    </div>

                  ))}


                  <div className="rounded-2xl bg-slate-800 text-white p-4 flex items-center justify-between gap-3">

                    <div>
                      <div className="text-xs text-slate-300 uppercase">
                        Total
                      </div>

                      <div className="text-xs text-slate-400 mt-1">
                        {selectedPurchase.items?.length || 0} producto(s)
                      </div>
                    </div>

                    <div className="text-xl font-bold whitespace-nowrap">
                      {money(selectedPurchase.total)}
                    </div>

                  </div>

                </div>

              </div>


              {/* RESPONSABLES */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">

                <div className="border rounded-2xl p-4 min-w-0">
                  <div className="text-xs text-slate-400 uppercase">
                    Comprador
                  </div>

                  <div className="font-medium mt-1 break-words">
                    {selectedPurchase.buyer_name || '—'}
                  </div>
                </div>


                <div className="border rounded-2xl p-4 min-w-0">
                  <div className="text-xs text-slate-400 uppercase">
                    Pagado por
                  </div>

                  <div className="font-medium mt-1 break-words">
                    {selectedPurchase.paid_by_name || '—'}
                  </div>
                </div>


                <div className="border rounded-2xl p-4 min-w-0">
                  <div className="text-xs text-slate-400 uppercase">
                    Registrado por
                  </div>

                  <div className="font-medium mt-1 break-words">
                    {selectedPurchase.created_by_name || '—'}
                  </div>
                </div>

              </div>


              {/* OBSERVACIONES */}
              <div className="border rounded-2xl p-4 md:p-5 mb-6">

                <div className="text-xs text-slate-400 uppercase mb-2">
                  Nota / Observaciones
                </div>

                <div className="text-slate-700 whitespace-pre-wrap break-words">
                  {selectedPurchase.notes || 'Sin observaciones'}
                </div>

              </div>


              {/* CERRAR */}
              <div className="flex justify-end pb-8 md:pb-2">

                <button
                  type="button"
                  onClick={() => setSelectedPurchase(null)}
                  className="w-full md:w-auto px-5 py-3 border rounded-xl hover:bg-slate-50"
                >
                  Cerrar
                </button>

              </div>

            </div>

          </div>

        </div>
      )}

      {tab === 'cotizador' && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <PurchaseQuote />
        </div>
      )}

      {tab === 'reportes' && (
        <div className="bg-white border rounded-xl overflow-hidden">

          {/* CABECERA */}
          <div className="p-4 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-3">

            <div className="min-w-0">
              <div className="font-semibold text-lg">
                Reporte de compras
              </div>

              <div className="text-sm text-slate-500">
                Consulta las compras realizadas por período, producto,
                proveedor y responsables.
              </div>
            </div>

            <button
              type="button"
              onClick={printReport}
              className="w-full md:w-auto px-4 py-3 border rounded-lg bg-white hover:bg-slate-50 flex items-center justify-center gap-2"
              title="Imprimir o guardar como PDF"
            >
              <Printer size={17} />
              Imprimir / PDF
            </button>

          </div>


          {/* CONTENIDO */}
          <div className="p-4 space-y-4">

            {/* FILTROS */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

              <label>
                <span className="text-sm text-slate-600">
                  Fecha desde
                </span>

                <input
                  type="date"
                  value={reportFilters.date_from}
                  onChange={e =>
                    setReportFilters({
                      ...reportFilters,
                      date_from: e.target.value
                    })
                  }
                  className="w-full border rounded-lg p-2 mt-1"
                />
              </label>


              <label>
                <span className="text-sm text-slate-600">
                  Fecha hasta
                </span>

                <input
                  type="date"
                  value={reportFilters.date_to}
                  onChange={e =>
                    setReportFilters({
                      ...reportFilters,
                      date_to: e.target.value
                    })
                  }
                  className="w-full border rounded-lg p-2 mt-1"
                />
              </label>


              <label>
                <span className="text-sm text-slate-600">
                  Producto
                </span>

                <select
                  value={reportFilters.product_id}
                  onChange={e =>
                    setReportFilters({
                      ...reportFilters,
                      product_id: e.target.value
                    })
                  }
                  className="w-full border rounded-lg p-2 mt-1"
                >
                  <option value="">Todos los productos</option>

                  {products
                    .slice()
                    .sort((a, b) =>
                      (a.name || '').localeCompare(
                        b.name || '',
                        'es'
                      )
                    )
                    .map(product => (
                      <option
                        key={product.id}
                        value={product.id}
                      >
                        {product.name}
                      </option>
                    ))}
                </select>

              </label>


              <label>
                <span className="text-sm text-slate-600">
                  Proveedor
                </span>

                <select
                  value={reportFilters.supplier_id}
                  onChange={e =>
                    setReportFilters({
                      ...reportFilters,
                      supplier_id: e.target.value
                    })
                  }
                  className="w-full border rounded-lg p-2 mt-1"
                >
                  <option value="">Todos los proveedores</option>

                  {suppliers
                    .slice()
                    .sort((a, b) =>
                      (a.name || '').localeCompare(
                        b.name || '',
                        'es'
                      )
                    )
                    .map(supplier => (
                      <option
                        key={supplier.id}
                        value={supplier.id}
                      >
                        {supplier.name}
                      </option>
                    ))}
                </select>

              </label>


              <label>
                <span className="text-sm text-slate-600">
                  Comprado por
                </span>

                <select
                  value={reportFilters.buyer_id}
                  onChange={e =>
                    setReportFilters({
                      ...reportFilters,
                      buyer_id: e.target.value
                    })
                  }
                  className="w-full border rounded-lg p-2 mt-1"
                >
                  <option value="">Todas las personas</option>

                  {users
                    .filter(u => u.status !== 'inactive')
                    .slice()
                    .sort((a, b) =>
                      (a.name || '').localeCompare(
                        b.name || '',
                        'es'
                      )
                    )
                    .map(user => (
                      <option
                        key={user.id}
                        value={user.id}
                      >
                        {user.name || user.email}
                      </option>
                    ))}
                </select>

              </label>


              <label>
                <span className="text-sm text-slate-600">
                  Pagado por
                </span>

                <select
                  value={reportFilters.paid_by_id}
                  onChange={e =>
                    setReportFilters({
                      ...reportFilters,
                      paid_by_id: e.target.value
                    })
                  }
                  className="w-full border rounded-lg p-2 mt-1"
                >
                  <option value="">Todas las personas</option>

                  {users
                    .filter(u => u.status !== 'inactive')
                    .slice()
                    .sort((a, b) =>
                      (a.name || '').localeCompare(
                        b.name || '',
                        'es'
                      )
                    )
                    .map(user => (
                      <option
                        key={user.id}
                        value={user.id}
                      >
                        {user.name || user.email}
                      </option>
                    ))}
                </select>

              </label>


              <label>
                <span className="text-sm text-slate-600">
                  Forma de pago
                </span>

                <select
                  value={reportFilters.payment_method}
                  onChange={e =>
                    setReportFilters({
                      ...reportFilters,
                      payment_method: e.target.value
                    })
                  }
                  className="w-full border rounded-lg p-2 mt-1"
                >
                  <option value="">Todas</option>
                  <option value="Contado">Contado</option>
                  <option value="Transferencia">Transferencia</option>
                  <option value="QR">QR</option>
                  <option value="Crédito">Crédito</option>
                  <option value="Otro">Otro</option>
                </select>

              </label>

            </div>


            {/* LIMPIAR */}
            <div className="flex justify-end">

              <button
                type="button"
                onClick={() =>
                  setReportFilters({
                    date_from: '',
                    date_to: '',
                    product_id: '',
                    supplier_id: '',
                    buyer_id: '',
                    paid_by_id: '',
                    payment_method: ''
                  })
                }
                className="w-full md:w-auto px-4 py-3 border rounded-lg hover:bg-slate-50"
              >
                Limpiar filtros
              </button>

            </div>


            {/* RESUMEN */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">

              <div className="border rounded-xl p-4 bg-slate-50">
                <div className="text-sm text-slate-500">
                  Compras
                </div>
                <div className="text-2xl font-bold">
                  {reportData.purchase_count}
                </div>
              </div>


              <div className="border rounded-xl p-4 bg-slate-50">
                <div className="text-sm text-slate-500">
                  Cantidad comprada
                </div>
                <div className="text-2xl font-bold">
                  {reportData.quantity}
                </div>
              </div>


              <div className="border rounded-xl p-4 bg-slate-50">
                <div className="text-sm text-slate-500">
                  Total comprado
                </div>
                <div className="text-2xl font-bold">
                  {money(reportData.total)}
                </div>
              </div>


              <div className="border rounded-xl p-4 bg-slate-50">
                <div className="text-sm text-slate-500">
                  Precio promedio
                </div>
                <div className="text-2xl font-bold">
                  {money(reportData.average_unit_price)}
                </div>
              </div>

            </div>


            {/* EXTREMOS DE PRECIO */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-2 md:gap-4">

              <div className="border rounded-xl p-4">
                <div className="text-sm text-slate-500">
                  Precio mínimo
                </div>

                <div className="text-xl font-semibold">
                  {money(reportData.min_unit_price)}
                </div>
              </div>


              <div className="border rounded-xl p-4">
                <div className="text-sm text-slate-500">
                  Precio máximo
                </div>

                <div className="text-xl font-semibold">
                  {money(reportData.max_unit_price)}
                </div>
              </div>

            </div>


            {/* DETALLE */}
            <div className="border rounded-xl overflow-hidden">

              <div className="p-4 border-b">
                <div className="font-semibold">
                  Detalle del reporte
                </div>

                <div className="text-xs text-slate-500">
                  {reportData.rows.length} registros encontrados
                </div>
              </div>


              {/* TABLA DESKTOP */}
              <div className="hidden md:block overflow-x-auto">

                <table className="w-full text-sm">

                  <thead className="bg-slate-50">

                    <tr>

                      <th className="text-left p-3 whitespace-nowrap">
                        Fecha
                      </th>

                      <th className="text-left p-3">
                        Producto
                      </th>

                      <th className="text-left p-3">
                        Proveedor
                      </th>

                      <th className="text-left p-3">
                        Factura
                      </th>

                      <th className="text-right p-3">
                        Cantidad
                      </th>

                      <th className="text-right p-3 whitespace-nowrap">
                        P. Unitario
                      </th>

                      <th className="text-right p-3 whitespace-nowrap">
                        Total
                      </th>

                    </tr>

                  </thead>


                  <tbody>

                    {reportData.rows.map((row, index) => (

                      <tr
                        key={`${row.purchase_id}-${row.product_id}-${index}`}
                        className="border-t hover:bg-slate-50"
                      >

                        <td className="p-3 whitespace-nowrap">
                          {formatPurchaseDate(row.purchase_date)}
                        </td>


                        <td className="p-3">

                          <div className="font-medium break-words">
                            {row.product_name || '—'}
                          </div>

                          {row.category && (
                            <div className="text-xs text-slate-500 break-words">
                              {row.category}
                            </div>
                          )}

                        </td>


                        <td className="p-3 break-words">
                          {row.supplier_name || '—'}
                        </td>


                        <td className="p-3 whitespace-nowrap">
                          {row.invoice_number || '—'}
                        </td>


                        <td className="p-3 text-right whitespace-nowrap">
                          {row.quantity} {row.unit}
                        </td>


                        <td className="p-3 text-right whitespace-nowrap">
                          {money(row.unit_price)}
                        </td>


                        <td className="p-3 text-right font-semibold whitespace-nowrap">
                          {money(row.total)}
                        </td>

                      </tr>

                    ))}


                    {!reportData.rows.length && (
                      <tr>

                        <td
                          colSpan="7"
                          className="p-8 text-center text-slate-500"
                        >
                          No hay compras que coincidan con los filtros seleccionados.
                        </td>

                      </tr>
                    )}

                  </tbody>

                </table>

              </div>


              {/* TARJETAS MOBILE */}
              <div className="md:hidden p-3 space-y-3">

                {reportData.rows.map((row, index) => (

                  <div
                    key={`${row.purchase_id}-${row.product_id}-${index}`}
                    className="border border-slate-200 rounded-2xl bg-white overflow-hidden"
                  >

                    <div className="p-4">

                      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                        Producto
                      </div>

                      <div className="mt-1 text-base font-bold text-slate-800 leading-snug break-words">
                        {row.product_name || '—'}
                      </div>

                      {row.category && (
                        <div className="mt-1 text-xs text-slate-500 break-words">
                          {row.category}
                        </div>
                      )}

                    </div>


                    <div className="border-t bg-slate-50 p-4 space-y-4">

                      <div className="grid grid-cols-2 gap-4">

                        <div>
                          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                            Fecha
                          </div>

                          <div className="mt-1 text-sm font-medium break-words">
                            {formatPurchaseDate(row.purchase_date)}
                          </div>
                        </div>


                        <div>
                          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                            Factura
                          </div>

                          <div className="mt-1 text-sm font-medium break-words">
                            {row.invoice_number || '—'}
                          </div>
                        </div>

                      </div>


                      <div>
                        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                          Proveedor
                        </div>

                        <div className="mt-1 text-sm font-medium text-slate-700 break-words">
                          {row.supplier_name || '—'}
                        </div>
                      </div>


                      <div className="grid grid-cols-2 gap-4">

                        <div>
                          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                            Cantidad
                          </div>

                          <div className="mt-1 text-sm font-semibold break-words">
                            {row.quantity} {row.unit}
                          </div>
                        </div>


                        <div>
                          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                            Precio unitario
                          </div>

                          <div className="mt-1 text-sm font-semibold whitespace-nowrap">
                            {money(row.unit_price)}
                          </div>
                        </div>

                      </div>


                      <div className="pt-3 border-t flex items-center justify-between gap-3">

                        <div>
                          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                            Total
                          </div>
                        </div>

                        <div className="text-lg font-bold text-slate-800 whitespace-nowrap">
                          {money(row.total)}
                        </div>

                      </div>

                    </div>

                  </div>

                ))}


                {!reportData.rows.length && (
                  <div className="border rounded-2xl p-8 text-center text-slate-500">
                    No hay compras que coincidan con los filtros seleccionados.
                  </div>
                )}

              </div>

            </div>

          </div>

        </div>
      )}

      {tab === 'productos' && (
        <div className="bg-white border rounded-xl overflow-hidden">

          <div className="p-4 border-b">
            <div className="relative">
              <Search size={17} className="absolute left-3 top-3 text-slate-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar producto, marca o número de parte..."
                className="w-full border rounded-lg pl-10 pr-3 py-3"
              />
            </div>
          </div>

          {/* TABLA ESCRITORIO */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3">Producto</th>
                  <th className="text-left p-3">Categoría</th>
                  <th className="text-left p-3">Marca / Parte</th>
                  <th className="text-center p-3">Unidad</th>
                  <th className="text-right p-3">Último precio</th>
                  <th className="text-right p-3">Promedio</th>
                  <th className="text-right p-3">Mínimo</th>
                  <th className="text-center p-3">Compras</th>
                </tr>
              </thead>

              <tbody>
                {filteredProducts.map(p => (
                  <tr key={p.id} className="border-t">
                    <td className="p-3 font-medium">{p.name}</td>
                    <td className="p-3">{p.category || '—'}</td>
                    <td className="p-3">
                      {p.brand || '—'}
                      {p.part_number ? ` · ${p.part_number}` : ''}
                    </td>
                    <td className="p-3 text-center">{p.unit}</td>
                    <td className="p-3 text-right">{money(p.last_price)}</td>
                    <td className="p-3 text-right">{money(p.avg_price)}</td>
                    <td className="p-3 text-right">{money(p.min_price)}</td>
                    <td className="p-3 text-center">{p.purchase_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* TARJETAS MOVIL */}
          <div className="md:hidden p-3 space-y-3">

            {filteredProducts.map(p => (
              <div
                key={p.id}
                className="border border-slate-200 rounded-2xl bg-white overflow-hidden"
              >

                <div className="p-4">
                  <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                    Producto
                  </div>

                  <div className="mt-1 text-base font-bold text-slate-800 leading-snug break-words">
                    {p.name || 'Sin nombre'}
                  </div>

                  {p.category && (
                    <div className="mt-1 text-xs text-slate-500 break-words">
                      {p.category}
                    </div>
                  )}
                </div>

                <div className="border-t bg-slate-50 p-4 space-y-4">

                  <div>
                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                      Marca / Número de parte
                    </div>

                    <div className="mt-1 text-sm font-medium text-slate-700 break-words">
                      {p.brand || '—'}
                      {p.part_number ? ` · ${p.part_number}` : ''}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">

                    <div>
                      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                        Unidad
                      </div>

                      <div className="mt-1 text-sm font-medium break-words">
                        {p.unit || '—'}
                      </div>
                    </div>

                    <div>
                      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                        Compras
                      </div>

                      <div className="mt-1 text-sm font-bold">
                        {p.purchase_count || 0}
                      </div>
                    </div>

                  </div>

                  <div className="grid grid-cols-3 gap-2">

                    <div className="bg-white rounded-xl border p-3 min-w-0">
                      <div className="text-[9px] font-semibold text-slate-400 uppercase leading-tight">
                        Último
                      </div>

                      <div className="mt-1 text-sm font-bold text-slate-800 whitespace-nowrap">
                        {money(p.last_price)}
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border p-3 min-w-0">
                      <div className="text-[9px] font-semibold text-slate-400 uppercase leading-tight">
                        Promedio
                      </div>

                      <div className="mt-1 text-sm font-bold text-slate-800 whitespace-nowrap">
                        {money(p.avg_price)}
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border p-3 min-w-0">
                      <div className="text-[9px] font-semibold text-slate-400 uppercase leading-tight">
                        Mínimo
                      </div>

                      <div className="mt-1 text-sm font-bold text-slate-800 whitespace-nowrap">
                        {money(p.min_price)}
                      </div>
                    </div>

                  </div>

                </div>

              </div>
            ))}

            {!filteredProducts.length && (
              <div className="border rounded-2xl p-8 text-center text-slate-500">
                No hay productos que coincidan con la búsqueda.
              </div>
            )}

          </div>

        </div>
      )}

      {tab === 'proveedores' && (
        <div className="bg-white border rounded-xl overflow-hidden">

          {/* TABLA ESCRITORIO */}
          <div className="hidden md:block overflow-x-auto">

            <table className="w-full text-sm">

              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3">Proveedor</th>
                  <th className="text-left p-3">NIT</th>
                  <th className="text-left p-3">Contacto</th>
                  <th className="text-left p-3">Dirección</th>
                </tr>
              </thead>

              <tbody>

                {suppliers.map(s => (
                  <tr key={s.id} className="border-t">

                    <td className="p-3 font-medium break-words">
                      {s.name || 'Sin nombre'}
                    </td>

                    <td className="p-3 break-words">
                      {s.nit || '—'}
                    </td>

                    <td className="p-3 break-words">
                      {s.phone || '—'}
                    </td>

                    <td className="p-3 break-words">
                      {s.address || '—'}
                    </td>

                  </tr>
                ))}

              </tbody>

            </table>

          </div>


          {/* TARJETAS MOVIL */}
          <div className="md:hidden p-3 space-y-3">

            {suppliers.map(s => (

              <div
                key={s.id}
                className="border border-slate-200 rounded-2xl bg-white overflow-hidden"
              >

                <div className="p-4">

                  <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                    Proveedor
                  </div>

                  <div className="mt-1 text-base font-bold text-slate-800 leading-snug break-words">
                    {s.name || 'Sin nombre'}
                  </div>

                </div>


                <div className="border-t bg-slate-50 p-4 space-y-4">

                  <div>
                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                      NIT
                    </div>

                    <div className="mt-1 text-sm font-medium text-slate-700 break-words">
                      {s.nit || '—'}
                    </div>
                  </div>


                  <div>
                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                      Contacto
                    </div>

                    <div className="mt-1 text-sm font-medium text-slate-700 break-words">
                      {s.phone || '—'}
                    </div>
                  </div>


                  <div>
                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                      Dirección
                    </div>

                    <div className="mt-1 text-sm font-medium text-slate-700 leading-relaxed break-words">
                      {s.address || '—'}
                    </div>
                  </div>

                </div>

              </div>

            ))}


            {!suppliers.length && (
              <div className="border rounded-2xl p-8 text-center text-slate-500">
                No hay proveedores registrados.
              </div>
            )}

          </div>

        </div>
      )}

      {showPurchase && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form
            onSubmit={savePurchase}
            className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[92vh] overflow-y-auto"
          >
            <div className="p-5 border-b flex justify-between">
              <div>
                <h2 className="text-xl font-bold">{editingPurchaseId ? "Editar compra" : "Registrar compra"}</h2>
                <p className="text-sm text-slate-500">
                  Una compra puede contener múltiples productos.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowPurchase(false);
                  setEditingPurchaseId(null);
                  setError('');
                }}
              >
                <X />
              </button>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <label>
                <span className="text-sm">Fecha</span>
                <input
                  type="date"
                  value={purchase.purchase_date}
                  onChange={e => setPurchase({...purchase, purchase_date: e.target.value})}
                  className="w-full border rounded-lg p-2"
                  required
                />
              </label>

              <label>
                <span className="text-sm">Proveedor</span>
                <select
                  value={
                    purchase.supplier_id ||
                    (purchase.supplier_name ? '__ai_supplier__' : '')
                  }
                  onChange={e => {
                    const value = e.target.value;

                    if (value === '__ai_supplier__') {
                      setPurchase({
                        ...purchase,
                        supplier_id: ''
                      });
                    } else {
                      const selected = suppliers.find(s => s.id === value);

                      setPurchase({
                        ...purchase,
                        supplier_id: value,
                        supplier_name: selected?.name || ''
                      });
                    }
                  }}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Sin proveedor</option>

                  {purchase.supplier_name &&
                    !purchase.supplier_id && (
                      <option value="__ai_supplier__">
                        Nuevo proveedor detectado: {purchase.supplier_name}
                      </option>
                    )}

                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </label>

              <label>
                <span className="text-sm">Nº factura</span>
                <input
                  value={purchase.invoice_number}
                  onChange={e => setPurchase({...purchase, invoice_number: e.target.value})}
                  className="w-full border rounded-lg p-2"
                />
              </label>

              <label>
                <span className="text-sm">Forma de pago</span>
                <select
                  value={purchase.payment_method}
                  onChange={e => setPurchase({...purchase, payment_method: e.target.value})}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Seleccionar</option>
                  <option>Contado</option>
                  <option>Transferencia</option>
                  <option>QR</option>
                  <option>Crédito</option>
                  <option>Otro</option>
                </select>
              </label>

              <label>
                <span className="text-sm">Comprado por</span>
                <select
                  value={purchase.buyer_id}
                  onChange={e => {
                    const user = users.find(u => u.id === e.target.value);

                    setPurchase({
                      ...purchase,
                      buyer_id: user?.id || '',
                      buyer_name: user?.name || ''
                    });
                  }}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Seleccionar persona</option>

                  {users
                    .filter(u => u.status !== 'inactive')
                    .sort((a, b) =>
                      (a.name || '').localeCompare(
                        b.name || '',
                        'es'
                      )
                    )
                    .map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name || u.email}
                      </option>
                    ))}
                </select>
              </label>

              <label>
                <span className="text-sm">Pagado por</span>
                <select
                  value={purchase.paid_by_id}
                  onChange={e => {
                    const user = users.find(u => u.id === e.target.value);

                    setPurchase({
                      ...purchase,
                      paid_by_id: user?.id || '',
                      paid_by_name: user?.name || ''
                    });
                  }}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Seleccionar persona</option>

                  {users
                    .filter(u => u.status !== 'inactive')
                    .sort((a, b) =>
                      (a.name || '').localeCompare(
                        b.name || '',
                        'es'
                      )
                    )
                    .map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name || u.email}
                      </option>
                    ))}
                </select>
              </label>
            </div>

            <div className="px-5 grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <label>
                <span className="text-sm">Pedido relacionado</span>
                <select
                  value={purchase.request_id}
                  onChange={e =>
                    setPurchase({
                      ...purchase,
                      request_id: e.target.value
                    })
                  }
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Sin pedido relacionado</option>

                  {requests.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.order_number || r.id}
                      {r.requester_name ? ` — ${r.requester_name}` : ''}
                      {r.status ? ` — ${r.status}` : ''}
                    </option>
                  ))}
                </select>
              </label>

            </div>

            <div className="px-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold">Ítems de la compra</h3>
                <button
                  type="button"
                  onClick={addItem}
                  className="px-3 py-2 bg-slate-100 rounded-lg"
                >
                  <Plus size={16} className="inline mr-1" /> Agregar ítem
                </button>
              </div>

              <div className="space-y-3">
                {purchase.items.map((item, index) => (
                  <div key={index} className="border rounded-xl p-3 bg-slate-50">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                      <div className="md:col-span-4">
                        <label className="text-xs">Producto del catálogo</label>
                        <select
                          value={item.product_id}
                          onChange={e => selectProduct(index, e.target.value)}
                          className="w-full border rounded-lg p-2 bg-white"
                        >
                          <option value="">Producto nuevo / escribir abajo</option>
                          {products.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="md:col-span-4">
                        <label className="text-xs">Nombre del producto</label>
                        <input
                          value={item.product_name}
                          onChange={e => updateItem(index, 'product_name', e.target.value)}
                          className="w-full border rounded-lg p-2"
                          required
                        />
                      </div>

                      <div className="md:col-span-1">
                        <label className="text-xs">Cantidad</label>
                        <input
                          type="number"
                          min="0.001"
                          step="0.001"
                          value={item.quantity}
                          onChange={e => updateItem(index, 'quantity', e.target.value)}
                          className="w-full border rounded-lg p-2"
                          required
                        />
                      </div>

                      <div className="md:col-span-1">
                        <label className="text-xs">Unidad</label>
                        <input
                          value={item.unit}
                          onChange={e => updateItem(index, 'unit', e.target.value)}
                          className="w-full border rounded-lg p-2"
                        />
                      </div>

                      <div className="md:col-span-1">
                        <label className="text-xs">Precio</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={e => updateItem(index, 'unit_price', e.target.value)}
                          className="w-full border rounded-lg p-2"
                          required
                        />
                      </div>

                      <div className="md:col-span-1 text-right">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          disabled={purchase.items.length === 1}
                          className="p-2 text-red-600 disabled:opacity-30"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    <div className="mt-2 text-right font-semibold">
                      Subtotal: {money(Number(item.quantity || 0) * Number(item.unit_price || 0))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">

              <label className="md:col-span-2">
                <span className="text-sm">Observaciones</span>
                <input
                  value={purchase.notes}
                  onChange={e => setPurchase({...purchase, notes: e.target.value})}
                  className="w-full border rounded-lg p-2"
                />
              </label>
            </div>

            <div className="p-3 sm:p-5 border-t flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sticky bottom-0 bg-white z-10">
              <div className="text-xl font-bold">
                Total: {money(total)}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowPurchase(false);
                    setEditingPurchaseId(null);
                    setError('');
                  }}
                  className="px-4 py-2 border rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-slate-800 text-white rounded-lg"
                >
                  <Save size={17} className="inline mr-1" /> {editingPurchaseId ? "Guardar cambios" : "Guardar compra"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {showProduct && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={saveProduct} className="bg-white rounded-xl p-6 w-full max-w-lg">
            <div className="flex justify-between mb-5">
              <h2 className="text-xl font-bold">Nuevo producto</h2>
              <button type="button" onClick={() => setShowProduct(false)}><X /></button>
            </div>

            <div className="space-y-3">
              <label className="block">
                <span className="text-sm">Nombre</span>
                <input
                  value={product.name || ''}
                  onChange={e => setProduct({...product, name: e.target.value})}
                  className="w-full border rounded-lg p-2"
                  required
                />
              </label>

              <label className="block">
                <span className="text-sm">Categoría</span>
                <select
                  value={product.category || ''}
                  onChange={e => setProduct({...product, category: e.target.value})}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Seleccionar categoría</option>
                  {PRODUCT_CATEGORIES.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-sm">Marca</span>
                <input
                  value={product.brand || ''}
                  onChange={e => setProduct({...product, brand: e.target.value})}
                  className="w-full border rounded-lg p-2"
                />
              </label>

              <label className="block">
                <span className="text-sm">Nº de parte</span>
                <input
                  value={product.part_number || ''}
                  onChange={e => setProduct({...product, part_number: e.target.value})}
                  className="w-full border rounded-lg p-2"
                />
              </label>

              <label className="block">
                <span className="text-sm">Unidad</span>
                <select
                  value={product.unit || ''}
                  onChange={e => setProduct({...product, unit: e.target.value})}
                  className="w-full border rounded-lg p-2"
                >
                  {UNITS.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowProduct(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
              <button type="submit" className="px-4 py-2 bg-slate-800 text-white rounded-lg">Guardar</button>
            </div>
          </form>
        </div>
      )}

      {showSupplier && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={saveSupplier} className="bg-white rounded-xl p-6 w-full max-w-lg">
            <div className="flex justify-between mb-5">
              <h2 className="text-xl font-bold">Nuevo proveedor</h2>
              <button type="button" onClick={() => setShowSupplier(false)}><X /></button>
            </div>

            <div className="space-y-3">
              {[
                ['name', 'Nombre'],
                ['nit', 'NIT'],
                ['phone', 'Contacto'],
                ['address', 'Dirección']
              ].map(([field, label]) => (
                <label key={field} className="block">
                  <span className="text-sm">{label}</span>
                  <input
                    type={field === 'phone' ? 'tel' : 'text'}
                    value={supplier[field] || ''}
                    onChange={e => setSupplier({...supplier, [field]: e.target.value})}
                    className="w-full border rounded-lg p-2"
                    placeholder={field === 'phone' ? 'Ej. 78511661' : ''}
                    required={field === 'name'}
                  />
                </label>
              ))}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowSupplier(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
              <button type="submit" className="px-4 py-2 bg-slate-800 text-white rounded-lg">Guardar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
